/**
 * "Something was just written on this device."
 *
 * The sync loop already polls, and a poll is fine for hearing about *other*
 * people's changes. It is not fine for the operator's own: tapping a soldier's
 * status and then waiting up to eight seconds before anybody else can see it is
 * exactly the lag the cloud was added to remove.
 *
 * So the stores announce their writes here and the loop pushes immediately. It
 * is a plain listener set rather than anything cleverer because there is only
 * ever one listener — the shell's sync hook — and the stores must not have to
 * know that, nor import React to say it.
 *
 * Announcing is deliberately cheap and deliberately ignorable: with no listener
 * attached (the healthcheck, a test, the app before sign-in) it does nothing at
 * all, so no store needs to guard the call.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Subscribes to local writes. Returns the unsubscribe, so a component that
 * mounts and unmounts repeatedly cannot leak a listener.
 */
export function onLocalWrite(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announces a local write.
 *
 * A listener that throws must not take the store's write down with it — the
 * data is already saved by the time this is called, and failing the mutation
 * because a background sync hiccuped would be the wrong way round.
 */
export function signalLocalWrite(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // The write stands; the mirror will catch up on its next poll.
    }
  }
}
